import { create } from "zustand";
import type {
  LiveChannel,
  LivePhase,
  RtmChatMessage,
} from "~/features/live/types";

const MAX_MESSAGES = 100;

type LiveState = {
  phase: LivePhase;
  currentChannel: LiveChannel | null;
  remoteUid: number | null;
  messages: RtmChatMessage[];
  currentPoint: number | null;
  durationSec: number;
  error: string | null;
  videoReady: boolean;
};

type LiveActions = {
  setPhase: (phase: LivePhase) => void;
  setCurrentChannel: (channel: LiveChannel | null) => void;
  setRemoteUid: (uid: number | null) => void;
  addMessage: (message: RtmChatMessage) => void;
  setCurrentPoint: (point: number | null) => void;
  incrementDuration: () => void;
  setError: (error: string | null) => void;
  setVideoReady: (ready: boolean) => void;
  reset: () => void;
};

const initialState: LiveState = {
  phase: "idle",
  currentChannel: null,
  remoteUid: null,
  messages: [],
  currentPoint: null,
  durationSec: 0,
  error: null,
  videoReady: false,
};

export const useLiveStore = create<LiveState & LiveActions>()((set) => ({
  ...initialState,
  setPhase: (phase) => set({ phase }),
  setCurrentChannel: (channel) => set({ currentChannel: channel }),
  setRemoteUid: (uid) => set({ remoteUid: uid }),
  addMessage: (message) =>
    set((state) => {
      const next = [...state.messages, message];
      return {
        messages: next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next,
      };
    }),
  setCurrentPoint: (point) => set({ currentPoint: point }),
  incrementDuration: () =>
    set((state) => ({ durationSec: state.durationSec + 1 })),
  setError: (error) => set({ error }),
  setVideoReady: (ready) => set({ videoReady: ready }),
  reset: () => set(initialState),
}));
